import { createAction } from "@reduxjs/toolkit";
import { normalize } from "normalizr";
import { arrayOfCalendarEventParticipant } from "~/schemas/schema.calendarEventParticipants.js";
import { calendarEvent } from "~/schemas/schema.calendarEvents";
import { arrayOfComments } from "~/schemas/schema.comments";
import * as actionTypes from "~/store/action_types";
import Angie from "~/utils/angie";
import { fetch } from "~/utils/fetch";
import * as trashActions from "./actions.trash.jsx";

export const fetchCalendarEvent = (calendarId, id) => {
  const url = `${Angie.api_url}/calendars/${calendarId}/events/${id}`;
  return {
    types: [
      actionTypes.FETCH_CALENDAR_EVENT_LOADING,
      actionTypes.FETCH_CALENDAR_EVENT_SUCCESS,
      actionTypes.FETCH_CALENDAR_EVENT_ERROR,
    ],
    callAPI: () =>
      fetch({
        url: url,
      }),
    payload: {
      id,
      calendarId,
      schema: {
        single: calendarEvent,
        comments: arrayOfComments,
        participants: arrayOfCalendarEventParticipant,
      },
    },
    cache: { key: url },
  };
};

export const prefetchCalendarEvent = (calendarId, id) => {
  return {
    types: [
      actionTypes.PREFETCH_CALENDAR_EVENT_LOADING,
      actionTypes.PREFETCH_CALENDAR_EVENT_SUCCESS,
      actionTypes.PREFETCH_CALENDAR_EVENT_ERROR,
    ],
    callAPI: () =>
      fetch({
        url: `${Angie.api_url}/calendars/${calendarId}/events/${id}`,
        prefetchNext: true,
      }),
    payload: {
      id,
      calendarId,
      schema: {
        single: calendarEvent,
        comments: arrayOfComments,
        participants: arrayOfCalendarEventParticipant,
      },
    },
  };
};

export const addCalendarEvent = (calendarId, attributes) => {
  return {
    types: [
      actionTypes.ADD_CALENDAR_EVENT_LOADING,
      actionTypes.ADD_CALENDAR_EVENT_SUCCESS,
      actionTypes.ADD_CALENDAR_EVENT_ERROR,
    ],
    callAPI: () =>
      fetch({
        url: `${Angie.api_url}/calendars/${calendarId}/events`,
        method: "POST",
        body: attributes,
      }),
    payload: {
      calendarId,
      attributes,
      schema: {
        single: calendarEvent,
        comments: arrayOfComments,
      },
    },
  };
};

export const editCalendarEvent = (id, calendarId, attributes) => {
  return {
    types: [
      actionTypes.EDIT_CALENDAR_EVENT_LOADING,
      actionTypes.EDIT_CALENDAR_EVENT_SUCCESS,
      actionTypes.EDIT_CALENDAR_EVENT_ERROR,
    ],
    callAPI: () =>
      fetch({
        url: `${Angie.api_url}/calendars/${calendarId}/events/${id}`,
        method: "PUT",
        body: attributes,
      }),
    payload: {
      id,
      calendarId,
      attributes,
      schema: {
        single: calendarEvent,
        comments: arrayOfComments,
      },
    },
  };
};

export const moveToTrash = (id) => {
  const objectType = `CalendarEvent`;
  return {
    ...trashActions.moveToTrash(objectType, id),
    types: [
      actionTypes.MOVE_CALENDAR_EVENT_TO_TRASH_LOADING,
      actionTypes.MOVE_CALENDAR_EVENT_TO_TRASH_SUCCESS,
      actionTypes.MOVE_CALENDAR_EVENT_TO_TRASH_ERROR,
    ],
    payload: {
      id,
      schema: {
        single: calendarEvent,
      },
    },
  };
};

export const restoredFromTrash = (id) => {
  const objectType = `CalendarEvent`;
  return {
    ...trashActions.restoredFromTrash(objectType, id),
    types: [
      actionTypes.RESTORED_CALENDAR_EVENT_FROM_TRASH_LOADING,
      actionTypes.RESTORED_CALENDAR_EVENT_FROM_TRASH_SUCCESS,
      actionTypes.RESTORED_CALENDAR_EVENT_FROM_TRASH_ERROR,
    ],
    payload: {
      id,
      schema: {
        single: calendarEvent,
      },
    },
  };
};

export const deleteCalendarEvent = (id) => {
  const objectType = `CalendarEvent`;
  return {
    ...trashActions.permanentlyDelete(objectType, id),
    types: [
      actionTypes.DELETE_CALENDAR_EVENT_LOADING,
      actionTypes.DELETE_CALENDAR_EVENT_SUCCESS,
      actionTypes.DELETE_CALENDAR_EVENT_ERROR,
    ],
    payload: {
      id,
    },
  };
};

export const setCalendarEventFromEventStream = createAction(
  actionTypes.SET_CALENDAR_EVENT_FROM_EVENT_STREAM,
  (data) => {
    return {
      payload: normalize(data, calendarEvent),
    };
  }
);
export const deleteCalendarEventFromEventStream = createAction(actionTypes.DELETE_CALENDAR_EVENT_FROM_EVENT_STREAM);
export const moveCalendarEventsToTrashFromEventStreamByCalendarId = createAction(
  actionTypes.MOVE_CALENDAR_EVENTS_TO_TRASH_FROM_EVENT_STREAM_BY_CALENDAR_ID
);

export const rescheduleOnlyThisEvent = (id, calendarId, attributes) => {
  return {
    types: [
      actionTypes.RESCHEDULE_ONLY_THIS_CALENDAR_EVENT_LOADING,
      actionTypes.RESCHEDULE_ONLY_THIS_CALENDAR_EVENT_SUCCESS,
      actionTypes.RESCHEDULE_ONLY_THIS_CALENDAR_EVENT_ERROR,
    ],
    callAPI: () =>
      fetch({
        url: `${Angie.api_url}/calendars/${calendarId}/events/${id}/reschedule/only-this`,
        method: "PUT",
        body: attributes,
      }),
    payload: {
      id,
      calendarId,
      attributes,
      schema: {
        single: calendarEvent,
      },
    },
  };
};

export const rescheduleThisAndFutureEvents = (id, calendarId, attributes) => {
  return {
    types: [
      actionTypes.RESCHEDULE_THIS_AND_FUTURE_CALENDAR_EVENTS_LOADING,
      actionTypes.RESCHEDULE_THIS_AND_FUTURE_CALENDAR_EVENTS_SUCCESS,
      actionTypes.RESCHEDULE_THIS_AND_FUTURE_CALENDAR_EVENTS_ERROR,
    ],
    callAPI: () =>
      fetch({
        url: `${Angie.api_url}/calendars/${calendarId}/events/${id}/reschedule/future`,
        method: "PUT",
        body: attributes,
      }),
    payload: {
      id,
      calendarId,
      attributes,
      schema: {
        single: calendarEvent,
      },
    },
  };
};

export const rescheduleAllEvents = (id, calendarId, attributes) => {
  return {
    types: [
      actionTypes.RESCHEDULE_ALL_CALENDAR_EVENTS_LOADING,
      actionTypes.RESCHEDULE_ALL_CALENDAR_EVENTS_SUCCESS,
      actionTypes.RESCHEDULE_ALL_CALENDAR_EVENTS_ERROR,
    ],
    callAPI: () =>
      fetch({
        url: `${Angie.api_url}/calendars/${calendarId}/events/${id}/reschedule/all`,
        method: "PUT",
        body: attributes,
      }),
    payload: {
      id,
      calendarId,
      attributes,
      schema: {
        single: calendarEvent,
      },
    },
  };
};

export const rescheduleExceptionEvent = (id, calendarId, attributes) => {
  return {
    types: [
      actionTypes.RESCHEDULE_EXCEPTION_CALENDAR_EVENT_LOADING,
      actionTypes.RESCHEDULE_EXCEPTION_CALENDAR_EVENT_SUCCESS,
      actionTypes.RESCHEDULE_EXCEPTION_CALENDAR_EVENT_ERROR,
    ],
    callAPI: () =>
      fetch({
        url: `${Angie.api_url}/calendars/${calendarId}/events/${id}/reschedule/exception`,
        method: "PUT",
        body: attributes,
      }),
    payload: {
      id,
      calendarId,
      attributes,
      schema: {
        single: calendarEvent,
      },
    },
  };
};

export const addCalendarEventsExcludedDates = (id, calendarId, attributes) => {
  return {
    types: [
      actionTypes.ADD_CALENDAR_EVENT_EXCLUDED_DATES_LOADING,
      actionTypes.ADD_CALENDAR_EVENT_EXCLUDED_DATES_SUCCESS,
      actionTypes.ADD_CALENDAR_EVENT_EXCLUDED_DATES_ERROR,
    ],
    callAPI: () =>
      fetch({
        url: `${Angie.api_url}/calendars/${calendarId}/events/${id}/exceptions`,
        method: "POST",
        body: attributes,
      }),
    payload: {
      id,
      calendarId,
      attributes,
    },
  };
};
